import { Component, OnInit, computed, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { ApiKeyScope, CreatedProjectApiKey, ProjectApiKey } from 'src/app/models/api-keys.model';
import { ProjectManagementService } from 'src/app/services/project-management.service';
import { ToastService } from 'src/app/services/toast.service';
import { environment } from 'src/environments/environment';

interface Endpoint {
  path: string;
  description: string;
}

@Component({
  selector: 'app-project-api-keys',
  templateUrl: './project-api-keys.component.html',
  styleUrls: ['./project-api-keys.component.scss'],
  standalone: false,
})
export class ProjectApiKeysPageComponent implements OnInit {
  readonly endpoints: Endpoint[] = [
    { path: 'issues', description: 'Error issues. Query: status, environment, platform, q, sort, page, page_size.' },
    { path: 'issues/{issue_uuid}', description: 'One issue with its latest event and stack trace.' },
    { path: 'issues/{issue_uuid}/events', description: 'Every occurrence of an issue.' },
    { path: 'issues/{issue_uuid}/events/{event_uuid}', description: 'One event in full.' },
    { path: 'stats', description: 'Error counts over the last 14 days.' },
    { path: 'transactions', description: 'Traced requests. Query: name, q, environment, status, user, min_duration_ms, sort=duration, page.' },
    { path: 'transactions/{transaction_uuid}', description: 'One transaction with its spans.' },
    { path: 'traces/{trace_id}', description: 'All transactions of a distributed trace.' },
    { path: 'tracing-stats', description: 'Latency and throughput summary.' },
  ];

  /** Set by the route: `/main/api-keys` manages organization keys, `/main/project/:uuid/api-keys` project keys. */
  scope: ApiKeyScope = 'project';
  projectId = '';
  readonly projectName = signal('');
  readonly loading = signal(true);
  readonly loadError = signal(false);
  /** The server's reason, e.g. that organization keys are for the owner and admins only. */
  readonly loadErrorMessage = signal<string | null>(null);
  readonly keys = signal<ProjectApiKey[]>([]);

  readonly newKeyName = signal('');
  readonly creating = signal(false);
  readonly createdKey = signal<CreatedProjectApiKey>(null);

  readonly confirmingRevoke = signal<string>(null);
  readonly revoking = signal<string>(null);
  readonly copied = signal<string>(null);

  readonly apiRoot = `${environment.baseUrl || window.location.origin}/api/v1`;
  readonly projectsUrl = `${this.apiRoot}/projects`;
  readonly apiBase = computed(() =>
    `${this.apiRoot}/error-tracking/projects/${this.isOrganization ? '{project_uuid}' : this.projectId}`,
  );
  readonly curlExample = computed(() =>
    `curl -H "Authorization: Bearer ${this.createdKey()?.key ?? '$PULSEWATCH_API_KEY'}" \\\n  ` +
    (this.isOrganization ? this.projectsUrl : `${this.apiBase()}/issues?status=unresolved`),
  );
  readonly agentPrompt = computed(() => [
    this.isOrganization
      ? 'Use the PulseWatch API to look at errors and traces of all projects in my organization.'
      : `Use the PulseWatch API to look at errors and traces of the project "${this.projectName()}".`,
    ...(this.isOrganization ? [`List the projects with GET ${this.projectsUrl} (each has a uuid).`] : []),
    `Base URL${this.isOrganization ? ' per project' : ''}: ${this.apiBase()}`,
    `Send the header "Authorization: Bearer ${this.createdKey()?.key ?? '$PULSEWATCH_API_KEY'}" with every request (read-only access).`,
    `Endpoints (GET): ${this.endpoints.map(e => e.path).join(', ')}.`,
    'Start with /issues?status=unresolved, then open the most frequent issues and their events to find the root cause.',
  ].join('\n'));

  constructor(
    private route: ActivatedRoute,
    private projectService: ProjectManagementService,
    private toast: ToastService,
  ) { }

  get isOrganization(): boolean {
    return this.scope === 'organization';
  }

  async ngOnInit() {
    this.scope = this.route.snapshot.data['scope'] ?? 'project';
    this.projectId = this.route.snapshot.paramMap.get('uuid') ?? '';
    try {
      if (this.isOrganization) {
        this.keys.set(await lastValueFrom(this.projectService.getOrganizationApiKeys()));
      } else {
        const [project, keys] = await Promise.all([
          lastValueFrom(this.projectService.getProject(this.projectId)),
          lastValueFrom(this.projectService.getApiKeys(this.projectId)),
        ]);
        this.projectName.set(project.name);
        this.keys.set(keys);
      }
    } catch (err: any) {
      this.loadError.set(true);
      this.loadErrorMessage.set(err?.detail ?? null);
    }
    this.loading.set(false);
  }

  async create() {
    const name = this.newKeyName().trim();
    if (!name || this.creating()) return;

    this.creating.set(true);
    try {
      const created = await lastValueFrom(
        this.isOrganization
          ? this.projectService.createOrganizationApiKey(name)
          : this.projectService.createApiKey(this.projectId, name),
      );
      const { key, ...listed } = created;
      this.createdKey.set(created);
      this.keys.update(keys => [listed, ...keys]);
      this.newKeyName.set('');
    } catch {
      this.toast.show('Failed to create API key', 'error');
    } finally {
      this.creating.set(false);
    }
  }

  async revoke(key: ProjectApiKey) {
    this.revoking.set(key.uuid);
    try {
      await lastValueFrom(
        this.isOrganization
          ? this.projectService.deleteOrganizationApiKey(key.uuid)
          : this.projectService.deleteApiKey(this.projectId, key.uuid),
      );
      this.keys.update(keys => keys.filter(k => k.uuid !== key.uuid));
      if (this.createdKey()?.uuid === key.uuid) this.createdKey.set(null);
      this.toast.show('API key revoked', 'success');
    } catch {
      this.toast.show('Failed to revoke API key', 'error');
    } finally {
      this.revoking.set(null);
      this.confirmingRevoke.set(null);
    }
  }

  async copy(text: string, what: string) {
    try {
      await navigator.clipboard.writeText(text);
      this.copied.set(what);
      setTimeout(() => this.copied() === what && this.copied.set(null), 2000);
    } catch {
      this.toast.show('Couldn’t copy — select the text and copy it manually', 'error');
    }
  }
}
